import { Box, Flex, Input, chakra } from '@chakra-ui/react';
import { Info, Search } from 'lucide-react';
import type { Dispatch, SetStateAction } from 'react';

import { categoryFilters, timeWindows, type CategoryFilter, type DashboardFilters, type SignalMode } from '../lib/filters';
import { SelectMenu, type SelectMenuOption } from './SelectMenu';

const categoryOptions: readonly SelectMenuOption<CategoryFilter>[] = categoryFilters.map((category) => ({
  value: category,
  label: category === 'all' ? 'All' : titleCase(category),
}));

const modeOptions: readonly SelectMenuOption<SignalMode>[] = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'watchlist', label: 'Watchlist' },
];

export function FilterBar({
  filters,
  onFiltersChange,
}: {
  filters: DashboardFilters;
  onFiltersChange: Dispatch<SetStateAction<DashboardFilters>>;
}) {
  return (
    <Box
      as="section"
      id="oddsquake-filters"
      mt="5"
      display="grid"
      gap="2"
      borderRadius="oqPanel"
      p="2"
      gridTemplateColumns={{ base: '1fr', md: 'auto auto auto minmax(260px,1fr)' }}
      layerStyle="glassPanel"
    >
      <Flex overflow="hidden" borderRadius="oqControl" borderWidth="1px" borderColor="border.subtle" bg="blackAlpha.100">
        <Box
          display="grid"
          placeItems="center"
          px="4"
          fontSize="sm"
          color="text.primary"
          title="How far back the move comparison looks."
        >
          <Box as="span" display="inline-flex" alignItems="center" gap="1.5" whiteSpace="nowrap">
            Time Window
            <Box as="span" color="text.muted" display="inline-flex" alignItems="center">
              <Info aria-label="How far back the move comparison looks." size={14} />
            </Box>
          </Box>
        </Box>
        {timeWindows.map((window) => {
          const active = filters.window === window;
          const hoverProps = active ? { _hover: { bg: 'whiteAlpha.50', color: 'text.primary' } } : {};
          return (
            <chakra.button
              key={window}
              type="button"
              onClick={() => {
                onFiltersChange((prev) => ({ ...prev, window }));
              }}
              minW="14"
              px="4"
              py="2.5"
              fontSize="sm"
              fontWeight="semibold"
              color={active ? 'text.primary' : 'text.muted'}
              bg={active ? 'signal.blue/20' : 'transparent'}
              boxShadow={
                active ? 'inset 0 0 0 1px var(--oq-colors-signal-blue)' : 'none'
              }
              transition="all 0.2s"
              {...hoverProps}
            >
              {window}
            </chakra.button>
          );
        })}
      </Flex>

      <SelectMenu
        label="Mode"
        value={filters.mode}
        options={modeOptions}
        onChange={(mode) => {
          onFiltersChange((prev) => ({ ...prev, mode }));
        }}
        helper="Confirmed signals clear the heat threshold. Watchlist shows near-misses."
      />

      <SelectMenu
        label="Category"
        value={filters.category}
        options={categoryOptions}
        onChange={(category) => {
          onFiltersChange((prev) => ({ ...prev, category }));
        }}
      />

      <Flex
        align="center"
        gap="3"
        borderRadius="oqControl"
        borderWidth="1px"
        borderColor="border.subtle"
        bg="blackAlpha.100"
        px="4"
      >
        <Box color="text.muted" display="inline-flex" alignItems="center">
          <Search size={16} />
        </Box>
        <Input
          value={filters.query}
          onChange={(event) => {
            onFiltersChange((prev) => ({ ...prev, query: event.target.value }));
          }}
          placeholder="Search markets..."
          variant="flushed"
          h="11"
          w="full"
          bg="transparent"
          fontSize="sm"
          color="text.primary"
          border="none"
          _placeholder={{ color: 'text.muted', opacity: 0.7 }}
        />
      </Flex>
    </Box>
  );
}

function titleCase(value: string) {
  return value.slice(0, 1).toUpperCase() + value.slice(1);
}